import { createSlice } from '@reduxjs/toolkit';
import { isConsumer, isWidget } from '../constants/appContext';
import { getURLSearchParams, setSearchParams } from '../utils/searchParamUtils';
import {
  ALL_DAY, CORE_DATE_PARAM, CORE_TIME_PARAM, ISO_DATE_FORMAT, CORE_SEATS_PARAM, CORE_SEATS_INIT_MAX, CORE_SEATS_INIT_MIN,
} from '../constants/resyCore';
import {
  getConformingCoreSeats, isConformingCoreISODate, isConformingCoreSeats, isConformingCoreSeatsMax, isConformingCoreSeatsMin, isConformingCoreTime, shouldCoreParamUpdate,
} from '../utils/resyCoreUtils';

// State types:
// - coreISODate is a ISO_8601 string YYYY-MM-DD
// - coreSeats is an integer
// - coreTime is a four-digit string

const resyCoreSlice = createSlice({
  name: 'resyCore',
  initialState: {
    appContext: null,
    didCoreISODateUpdate: false,
    didCoreSeatsUpdate: false,
    didCoreTimeUpdate: false,
    initCoreSeatsMax: CORE_SEATS_INIT_MAX,
    initCoreSeatsMin: CORE_SEATS_INIT_MIN,
    isLargePartyConfigured: false,
    previousCoreISODate: null,
    previousCoreSeats: null,
    previousCoreTime: null,
    selectedCoreISODate: null,
    selectedCoreSeats: null,
    selectedCoreSeatsMax: CORE_SEATS_INIT_MAX,
    selectedCoreSeatsMin: CORE_SEATS_INIT_MIN,
    selectedCoreTime: null,
    serviceConfigMap: {},
  },
  reducers: {
    initCoreSeatsMinMax: (state, { payload }) => {
      const { min, max } = payload;
      const minInteger = Number.parseInt(min, 10);
      /* istanbul ignore else */
      if (isConformingCoreSeatsMin(minInteger)) {
        state.initCoreSeatsMin = minInteger;
      }
      const maxInteger = Number.parseInt(max, 10);
      /* istanbul ignore else */
      if (isConformingCoreSeatsMax(maxInteger)) {
        state.initCoreSeatsMax = maxInteger;
      }
    },
    setAppContext: (state, { payload }) => {
      /* istanbul ignore else */
      if (isConsumer(payload) || isWidget(payload)) {
        state.appContext = payload;
      }
    },
    setSelectedCoreISODate: (state, { payload }) => {
      const isoDate = payload;
      state.didCoreISODateUpdate = false;
      if (isConformingCoreISODate(isoDate) && shouldCoreParamUpdate(state.selectedCoreISODate, isoDate)) {
        state.previousCoreISODate = state.selectedCoreISODate;
        state.selectedCoreISODate = isoDate;
        state.didCoreISODateUpdate = true;
      }
    },
    setisLargePartyConfigured: (state, { payload }) => {
      state.isLargePartyConfigured = payload;
    },
    setSelectedCoreSeats: (state, { payload }) => {
      const seatsInteger = Number.parseInt(payload, 10);
      const {
        selectedCoreSeats, selectedCoreSeatsMax, selectedCoreSeatsMin, isLargePartyConfigured,
      } = state;
      state.didCoreSeatsUpdate = false;
      /* istanbul ignore else */
      if (isConformingCoreSeats(seatsInteger, selectedCoreSeatsMin, selectedCoreSeatsMax, isLargePartyConfigured)
        && shouldCoreParamUpdate(selectedCoreSeats, seatsInteger)) {
        state.previousCoreSeats = selectedCoreSeats;
        state.selectedCoreSeats = seatsInteger;
        state.didCoreSeatsUpdate = true;
      }
    },
    setSelectedCoreSeatsMax: (state, { payload }) => {
      const maxInteger = Number.parseInt(payload, 10);
      if (isConformingCoreSeatsMax(maxInteger)) {
        state.selectedCoreSeatsMax = maxInteger;
      }
    },
    setSelectedCoreSeatsMin: (state, { payload }) => {
      const minInteger = Number.parseInt(payload, 10);
      if (isConformingCoreSeatsMin(minInteger)) {
        state.selectedCoreSeatsMin = minInteger;
      }
    },
    setSelectedCoreTime: (state, { payload }) => {
      const timeString = payload;
      state.didCoreTimeUpdate = false;
      const nextState = isConformingCoreTime(timeString) ? timeString : ALL_DAY;
      if (shouldCoreParamUpdate(nextState, state.selectedCoreTime)) {
        state.previousCoreTime = state.selectedCoreTime;
        state.selectedCoreTime = nextState;
        state.didCoreTimeUpdate = true;
      }
    },
    setServiceConfig: (state, { payload }) => {
      const { name, config } = payload;
      state.serviceConfigMap[name] = config;
    },
    setNonConformalCoreSeats: (state, { payload }) => {
      const seatsInteger = Number.parseInt(payload, 10);
      const { selectedCoreSeats } = state;
      state.didCoreSeatsUpdate = false;

      if (shouldCoreParamUpdate(selectedCoreSeats, seatsInteger)) {
        state.previousCoreSeats = selectedCoreSeats;
        state.selectedCoreSeats = seatsInteger;
        state.didCoreSeatsUpdate = true;
      }
    },
  },
});
const {
  setAppContext, initCoreSeatsMinMax, setSelectedCoreISODate, setSelectedCoreSeats,
  setSelectedCoreSeatsMax, setSelectedCoreSeatsMin, setSelectedCoreTime,
  setisLargePartyConfigured, setServiceConfig, setNonConformalCoreSeats,
} = resyCoreSlice.actions;

const setSelectedCoreSeatsMinMax = ({ min, max, isLargePartyConfigured = false }) => (dispatch, getState) => {
  dispatch(setSelectedCoreSeatsMin(min));
  dispatch(setSelectedCoreSeatsMax(max));
  dispatch(setisLargePartyConfigured(isLargePartyConfigured));
  const { resyCore } = getState();
  const { selectedCoreSeats, selectedCoreSeatsMax, selectedCoreSeatsMin } = resyCore;
  const isAboveMax = !!selectedCoreSeats && selectedCoreSeats > selectedCoreSeatsMax;
  const isBelowMin = !!selectedCoreSeats && selectedCoreSeats < selectedCoreSeatsMin;
  const isLargeParty = isLargePartyConfigured && isAboveMax;

  // reset selected if needed, based on new min/max values
  let seatsReset = selectedCoreSeatsMin;
  if (isBelowMin) {
    dispatch(setSelectedCoreSeats(seatsReset));
  }
  if (isAboveMax) {
    if (isLargeParty) {
      seatsReset = selectedCoreSeatsMax + 1;
    } else {
      seatsReset = selectedCoreSeatsMax;
    }
    dispatch(setSelectedCoreSeats(seatsReset));
  }
};

const initCoreISODateThunk = () => (dispatch) => {
  const searchParams = getURLSearchParams();
  const dateParam = searchParams.get(CORE_DATE_PARAM);
  let selectedCoreISODate = moment().format(ISO_DATE_FORMAT);

  if (isConformingCoreISODate(dateParam)) {
    selectedCoreISODate = dateParam;
  } else {
    setSearchParams(CORE_DATE_PARAM, null);
  }
  dispatch(setSelectedCoreISODate(selectedCoreISODate));
};

// Front-end initialization of seats from search params
// before confirming min/max within resyConfig promise chain.
// Nonconformal initialization is for Widget, where strict check on app load is not needed.
const initCoreSeatsThunk = (isConformal = true) => (dispatch, getState) => {
  const { resyCore: { initCoreSeatsMin, initCoreSeatsMax } } = getState();
  const searchParams = getURLSearchParams();
  let coreSeats = searchParams.get(CORE_SEATS_PARAM);
  if (isConformal || !coreSeats) {
    coreSeats = getConformingCoreSeats(coreSeats, initCoreSeatsMin, initCoreSeatsMax);
    dispatch(setSelectedCoreSeats(coreSeats));
  } else {
    dispatch(setNonConformalCoreSeats(coreSeats));
  }
};

// Initialization / confirmation of min/max values from resyConfig.
// Updates only if resyConfig is different than initCoreSeatsMin/Max defaults defined here.
// Permits external update of app-wide min/max values, but these values rarely change.
const initCoreSeatsMinMaxThunk = () => (dispatch, getState) => {
  const { resyConfig, resyCore } = getState();
  const configMin = resyConfig?.config?.min_party_size;
  const configMax = resyConfig?.config?.max_party_size;

  if ((configMin && configMin !== resyCore.initCoreSeatsMin)
    || (configMax && configMax !== resyCore.initCoreSeatsMax)) {
    dispatch(initCoreSeatsMinMax({ min: configMin, max: configMax }));
    dispatch(setSelectedCoreSeatsMinMax({ min: configMin, max: configMax }));
  }
};

const initCoreTimeThunk = () => (dispatch) => {
  const params = getURLSearchParams();
  const timeParam = params.get(CORE_TIME_PARAM);
  dispatch(setSelectedCoreTime(timeParam));
};

const resetSelectedCoreSeatsMinMax = () => (dispatch, getState) => {
  const { resyCore } = getState();
  const { initCoreSeatsMin, initCoreSeatsMax } = resyCore;
  /* istanbul ignore else */
  if (initCoreSeatsMin != null && initCoreSeatsMax != null) {
    dispatch(setSelectedCoreSeatsMinMax({ min: initCoreSeatsMin, max: initCoreSeatsMax }));
  }
};

// getter thunks intended for use in Angular controllers, and to be removed eventually.
const getSelectedCoreISODateThunk = () => (dispatch, getState) => {
  const { resyCore } = getState();
  return resyCore.selectedCoreISODate;
};

const getSelectedCoreSeatsThunk = () => (dispatch, getState) => {
  const { resyCore } = getState();
  return resyCore.selectedCoreSeats;
};

const getSelectedCoreSeatsMinMaxThunk = () => (dispatch, getState) => {
  const { resyCore } = getState();
  return { min: resyCore.selectedCoreSeatsMin, max: resyCore.selectedCoreSeatsMax };
};

const getSelectedCoreTimeThunk = () => (dispatch, getState) => {
  const { resyCore } = getState();
  return resyCore.selectedCoreTime;
};

export default resyCoreSlice.reducer;
export {
  getSelectedCoreISODateThunk,
  getSelectedCoreSeatsThunk,
  getSelectedCoreSeatsMinMaxThunk,
  getSelectedCoreTimeThunk,
  initCoreISODateThunk,
  initCoreSeatsThunk,
  initCoreSeatsMinMaxThunk,
  initCoreTimeThunk,
  resetSelectedCoreSeatsMinMax,
  setAppContext,
  setSelectedCoreISODate,
  setSelectedCoreSeats,
  setisLargePartyConfigured,
  setSelectedCoreSeatsMinMax,
  setSelectedCoreTime,
  setServiceConfig,
};
